import { BACKEND_API_URL, authHeaders } from "@/lib/api"

export type AuditKind = "surface" | "fragment" | "document" | "tools" | "template"

export type LiveState = "loaded" | "absent" | "not_loaded" | "repo_only"

export type PromptSummary = {
  id: string
  title: string
  group: string
  kind: AuditKind
  source: string
  sha256: string
  chars: number
  approx_tokens: number
  drift: boolean
  live_state: LiveState | null
  tool_count: number | null
  included_in: string[]
  error: string | null
  open_comments: number
}

export type PromptPart = { label: string; start: number; end: number; source: string }

export type PromptDetail = Omit<PromptSummary, "open_comments"> & {
  description: string
  text: string
  parts: PromptPart[]
  contains: PromptPart[]
  wire_sha256: string | null
  repo_sha256: string | null
  repo_chars: number | null
  live_source: string
  templated_in: string[]
  tokens: number | null
}

export type PromptIndex = {
  entries: PromptSummary[]
  deploy: { commit_sha: string | null; assembled_at: string }
  comments_available: boolean
}

export type AnchorStatus = "current" | "moved" | "outdated"

export type CommentView = {
  id: string
  body: string
  author_email: string
  created_at: string
  mine: boolean
}

export type Thread = CommentView & {
  quote: string
  resolved_at: string | null
  resolved_by_email: string | null
  anchor: { status: AnchorStatus; start: number | null; end: number | null }
  replies: CommentView[]
}

export type NewComment = { content_sha256: string; start: number; end: number; quote: string; body: string }

export class PromptAuditError extends Error {
  status: number
  constructor(status: number, code: string) {
    super(code)
    this.status = status
  }
}

const BASE = `${BACKEND_API_URL}/admin/prompt-audit`

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, { ...init, headers: await authHeaders() })
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new PromptAuditError(response.status, typeof body.detail === "string" ? body.detail : "request_failed")
  }
  return response.status === 204 ? (undefined as T) : response.json()
}

export const promptAuditKeys = {
  index: ["prompt-audit"] as const,
  detail: (id: string) => ["prompt-audit", "entry", id] as const,
  threads: (id: string) => ["prompt-audit", "threads", id] as const,
  search: (query: string) => ["prompt-audit", "search", query] as const,
}

export const getPromptIndex = () => request<PromptIndex>("")

export const searchPrompts = (query: string) =>
  request<{ query: string; hits: Record<string, number> }>(`/search?q=${encodeURIComponent(query)}`)

export const getPrompt = (id: string) => request<PromptDetail>(`/entries/${encodeURIComponent(id)}`)

export const getThreads = (id: string) =>
  request<{ prompt_id: string; sha256: string; threads: Thread[] }>(`/entries/${encodeURIComponent(id)}/comments`)

export const createComment = (id: string, comment: NewComment) =>
  request<Thread>(`/entries/${encodeURIComponent(id)}/comments`, { method: "POST", body: JSON.stringify(comment) })

export const replyToThread = (threadId: string, body: string) =>
  request<CommentView>(`/comments/${threadId}/replies`, { method: "POST", body: JSON.stringify({ body }) })

export const setThreadResolved = (threadId: string, resolved: boolean) =>
  request<{ id: string }>(`/comments/${threadId}/${resolved ? "resolve" : "reopen"}`, { method: "POST" })

export const deleteComment = (commentId: string) =>
  request<void>(`/comments/${commentId}`, { method: "DELETE" })

const ERROR_COPY: Record<string, string> = {
  prompt_changed: "This prompt changed since you opened it. Reload it and select the text again.",
  quote_mismatch: "The selection no longer matches the prompt text. Select it again.",
  prompt_comments_table_missing: "Comments are not set up on this environment yet.",
  not_your_comment: "You can only delete your own comments.",
}

export function auditErrorMessage(error: unknown): string {
  if (error instanceof PromptAuditError) {
    if (error.status === 401) return "Sign in again to comment."
    return ERROR_COPY[error.message] ?? "Something went wrong. Try again."
  }
  return "Something went wrong. Try again."
}
